import { showToast } from '../../engine/toast';
import { t, type Translations } from '../../engine/i18n/i18n';
import { unitArtFor } from '../../engine/unit-art';
import { icon } from '../../engine/icons';
import { formatMarchTime } from '../battle/march-format';
import {
  fetchFormations,
  fetchRoster,
  previewAttack,
  sendArmy,
  type AttackUnitInput,
  type FormationDef,
  type FormationId,
  type PreviewResult,
  type RosterEntry,
  type UnitType,
} from '../battle/battle-api';
import type { WorldMapPoi } from './worldmap-api';

/**
 * "Send Army" modal, opened from the world map popover's Send Army button
 * (worldmap-screen.ts). Copies colony-screen.ts's modal idiom (a fixed
 * backdrop appended to `root` — deliberately *not* to `#worldmap-wrap`, which
 * is `relative overflow-hidden` and already hosts the tap popover — with a
 * `data-modal-body` div re-rendered in place, backdrop-click-to-close).
 *
 * Two-step flow: composition (pick units, watch a live, debounced preview of
 * YOUR force and its march) -> confirmation (restate the choice, then commit).
 * No odds and no outcome are ever shown here: the preview says nothing about
 * the target (that is earned via recon), and march-then-resolve means the
 * fight has not happened yet — see battle-screen.ts's history list for that.
 */

const PREVIEW_DEBOUNCE_MS = 350;

/**
 * Display-only mirror of backend battle.formulas.ts's UNIT_CARRY_CAPACITY —
 * the server is the sole authority on the actual haul; this only lets the
 * per-row chip and the footer total render without a round trip per
 * keystroke (the force-wide total is still re-confirmed by the server via
 * `preview.carryCapacity` on every debounced preview).
 */
const UNIT_CARRY_CAPACITY: Record<UnitType, number> = {
  ATTACK_ROVER: 80,
  DEFENCE_BUNKER: 10,
  LIGHT_INFANTRY: 25,
  DEFENCE_TURRET_WALKER: 40,
  ATTACK_GUNSHIP: 50,
  SCOUT_BIKE: 0,
  SIEGE_ARTILLERY: 0,
  SIEGE_MORTAR_TANK: 0,
  SETTLER_CONVOY: 0,
};

/**
 * Shared glossary names where the English matches; the four units whose
 * glossary name differs from the plain title-cased type (e.g. "Sector Holder"
 * for DEFENCE_BUNKER) keep their own worldmap.unit.* keys so English output
 * here stays unchanged.
 */
const OWN_UNIT_KEYS: Partial<Record<UnitType, keyof Translations>> = {
  DEFENCE_BUNKER: 'worldmap.unit.DEFENCE_BUNKER',
  DEFENCE_TURRET_WALKER: 'worldmap.unit.DEFENCE_TURRET_WALKER',
  SIEGE_ARTILLERY: 'worldmap.unit.SIEGE_ARTILLERY',
  SIEGE_MORTAR_TANK: 'worldmap.unit.SIEGE_MORTAR_TANK',
};

function formatUnitLabel(unitType: UnitType): string {
  return t(OWN_UNIT_KEYS[unitType] ?? (`term.unit.${unitType}` as keyof Translations));
}

// formatMarchTime now lives in ../battle/march-format.ts — it is shared with
// the Battle history screen, and both local copies predated hours-long
// marches (neither had an hours branch). Re-exported because this module's
// tests and callers already import it from here.
export { formatMarchTime };

/**
 * Target display label: true barbarian -> "Barbarian", abandoned+unowned
 * -> "Abandoned", else the owner's username, else "Unclaimed".
 *
 * A bot is deliberately NOT special-cased here: it reads exactly like a real
 * player's colony (its own `ownerUsername`, drawn from the bot-identity
 * pool — see bot-identity.constants.ts), so a commander scouting the map has
 * no way to tell a bot colony from a human one by its label alone.
 *
 * A commander's equipped title is appended when they wear one, so a
 * barbarian camp reads as an individual ("Barbarian - Rust Marshal") rather
 * than one of many identical rows.
 */
export function targetOwnerName(poi: WorldMapPoi): string {
  // A Crimson Dust cave is a Colony row so an army can march at it, but it is
  // not a settlement and has no owner, no name and no status of its own — so
  // every branch below would fall through to "Unclaimed", which is what an
  // EMPTY PLOT says. Named explicitly instead.
  if (poi.kind === 'RESOURCE_NODE') return t('term.caveName');
  return poi.isBarbarian
    ? t('term.barbarian')
    : poi.ownerUsername
      ? poi.ownerUsername
      : poi.colonyStatus === 'ABANDONED'
        ? t('worldmap.owner.abandoned')
        : t('worldmap.owner.unclaimed');
}

/** Whether this POI's owner can wear a title at all: "Unclaimed" is an empty plot, and a cave has no owner. */
export function targetHasOwnerTitle(poi: WorldMapPoi): boolean {
  if (poi.kind === 'RESOURCE_NODE') return false;
  const unclaimed = !poi.isBarbarian && !poi.ownerUsername && poi.colonyStatus !== 'ABANDONED';
  return !unclaimed && !!poi.ownerTitle;
}

export function targetOwnerLabel(poi: WorldMapPoi): string {
  const base = targetOwnerName(poi);
  return targetHasOwnerTitle(poi) ? `${base} \u00b7 ${poi.ownerTitle}` : base;
}

export interface SendArmyModalHandle {
  close: () => void;
}

export function openSendArmyModal(
  root: HTMLElement,
  poi: WorldMapPoi,
  onSent: () => void,
): SendArmyModalHandle {
  let closed = false;
  let step: 'compose' | 'confirm' = 'compose';
  let roster: RosterEntry[] = [];
  let rosterLoaded = false;
  /** Chosen quantities survive step changes and roster re-renders — keyed so Cancel-from-confirm restores them. */
  const quantities = new Map<UnitType, number>();
  let preview: PreviewResult | null = null;
  let previewLoading = false;
  let previewError = false;
  /** Monotonic counter guarding against a stale (out-of-order) preview response clobbering a newer one — no AbortController idiom exists elsewhere in this codebase, so this mirrors the inFlight-boolean pattern colony-screen.ts uses for its own async guards. */
  let previewSeq = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let sending = false;
  /** The selectable march orders, from the server. Null (fetch failed / not yet loaded) hides the selector: the march is Balanced. */
  let formations: FormationDef[] | null = null;
  let formation: FormationId = 'BALANCED';

  const el = document.createElement('div');
  el.className = 'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.innerHTML = `
    <!-- The panel carries the entrance transform/blur and never scrolls itself (that breaks touch scrolling on iOS);
         the body inside is the scroller. dvh = the visible viewport. -->
    <div data-modal-panel
      class="popup-enter w-full sm:max-w-md max-h-[90dvh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface border border-border border-t-2 border-t-gold rounded-t-xl sm:rounded-xl pt-4">
      <!-- Body = the one scroller: vertical only, thin themed bar. The Cancel/Start bar is sticky inside it (see .sa-actions). -->
      <div data-modal-body class="themed-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-4" style="-webkit-overflow-scrolling: touch"></div>
    </div>
  `;
  el.addEventListener('click', (e) => {
    if (e.target === el) close();
  });
  root.appendChild(el);

  function close(): void {
    if (closed) return;
    closed = true;
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    el.remove();
  }

  function chosenUnits(): AttackUnitInput[] {
    const units: AttackUnitInput[] = [];
    quantities.forEach((quantity, unitType) => {
      if (quantity > 0) units.push({ unitType, quantity });
    });
    return units;
  }

  function headerHtml(title: string): string {
    return `
      <div class="flex items-start justify-between gap-3 mb-3">
        <div class="min-w-0">
          <h2 class="font-heading text-gold text-base truncate">${title}</h2>
          <p class="text-xs text-text-dim">${poi.colonyName ?? t('worldmap.send.colonyAt', { x: poi.x, y: poi.y })} &middot; (${poi.x}, ${poi.y}) &middot; ${targetOwnerLabel(poi)}</p>
        </div>
        <button type="button" data-action="close" aria-label="${t('common.close')}"
          class="min-w-11 min-h-11 -m-2 flex items-center justify-center text-text-dim hover:text-text leading-none text-xl shrink-0">&times;</button>
      </div>
    `;
  }

  /** "−20%" / "+10%" from a multiplier — the sign carries the direction, the colour carries good/bad. */
  function signedPct(factor: number): string {
    const pct = Math.round((factor - 1) * 100);
    return `${pct > 0 ? '+' : pct < 0 ? '\u2212' : ''}${Math.abs(pct)}%`;
  }

  /** What a formation changes relative to Balanced, as chips. `good` is from the attacker's side (less march time is good). */
  function formationEffects(def: FormationDef): { text: string; good: boolean }[] {
    const base = formations?.find((f) => f.id === 'BALANCED');
    const fx: { text: string; good: boolean }[] = [];
    if (def.marchTimeFactor !== 1) fx.push({ text: t('worldmap.formation.fx.march', { pct: signedPct(def.marchTimeFactor) }), good: def.marchTimeFactor < 1 });
    if (def.attackFactor !== 1) fx.push({ text: t('worldmap.formation.fx.attack', { pct: signedPct(def.attackFactor) }), good: def.attackFactor > 1 });
    if (def.carryFactor !== 1) fx.push({ text: t('worldmap.formation.fx.carry', { pct: signedPct(def.carryFactor) }), good: def.carryFactor > 1 });
    if (base && def.lootShare !== base.lootShare) {
      fx.push({ text: t('worldmap.formation.fx.loot', { pct: Math.round(def.lootShare * 100) }), good: def.lootShare > base.lootShare });
    }
    return fx;
  }

  function formationName(id: FormationId): string {
    return t(`worldmap.formation.${id}.name` as keyof Translations);
  }

  /** Card art per formation (generated, see public/formations). */
  const FORMATION_ART: Record<FormationId, string> = {
    BALANCED: '/formations/balanced.webp',
    FORCED_MARCH: '/formations/forced-march.webp',
    ASSAULT: '/formations/assault.webp',
    PLUNDER: '/formations/plunder.webp',
  };

  function formationOptionHtml(def: FormationDef): string {
    const selected = def.id === formation;
    const chips = formationEffects(def)
      .map((fx) => `<span class="formation-fx ${fx.good ? 'formation-fx--good' : 'formation-fx--bad'}">${fx.text}</span>`)
      .join('');
    return `
      <button type="button" role="radio" aria-checked="${selected}" data-formation="${def.id}"
        class="formation-opt${selected ? ' formation-opt--on' : ''} min-h-11 min-w-0 flex flex-col text-left rounded border overflow-hidden transition-colors duration-[120ms]"
        style="touch-action: manipulation;">
        <span class="formation-art block relative shrink-0">
          <img src="${FORMATION_ART[def.id]}" alt="" aria-hidden="true" loading="lazy" draggable="false" class="block w-full h-full object-cover" />
          ${selected ? `<span class="formation-tag" data-formation-selected>${icon('check')}<span>${t('worldmap.formation.selected')}</span></span>` : ''}
        </span>
        <span class="block flex-1 min-w-0 px-2.5 pt-1.5 pb-2">
          <span class="block text-sm text-text font-bold">${formationName(def.id)}</span>
          <span class="block text-[10px] text-text-dim leading-snug">${t(`worldmap.formation.${def.id}.desc` as keyof Translations)}</span>
          <span class="flex flex-wrap gap-1 mt-1">${chips || `<span class="formation-fx">${t('worldmap.formation.fx.none')}</span>`}</span>
        </span>
      </button>`;
  }

  function formationSelectorHtml(): string {
    if (!formations || formations.length === 0) return '';
    // Recon-only forces ignore the choice: the server answers with Balanced, which shows up as a mismatch here.
    const ignored = preview !== null && preview.formation !== formation;
    return `
      <div class="mt-3 pt-3 border-t border-border" data-formation-block>
        <div class="text-xs text-text-dim mb-1.5" id="send-army-formation-label">${t('worldmap.formation.label')}</div>
        <div role="radiogroup" aria-labelledby="send-army-formation-label" class="grid grid-cols-1 min-[420px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2" id="send-army-formation">
          ${formations.map(formationOptionHtml).join('')}
        </div>
        ${ignored ? `<p class="text-[11px] text-text-dim mt-1.5" data-formation-ignored>${t('worldmap.formation.reconIgnores')}</p>` : ''}
      </div>
    `;
  }

  function wireFormationBlock(): void {
    el.querySelectorAll('[data-formation]').forEach((node) => {
      node.addEventListener('click', () => {
        const id = (node as HTMLElement).dataset.formation as FormationId;
        if (id === formation) return;
        formation = id;
        renderFormationBlockOnly();
        // A new order changes march time, power and carry — re-ask the server straight away, no debounce: this is a click, not typing.
        if (debounceTimer !== null) clearTimeout(debounceTimer);
        void runPreview();
      });
    });
  }

  function renderFormationBlockOnly(): void {
    const block = el.querySelector('[data-formation-block]');
    if (!block) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = formationSelectorHtml();
    const next = tmp.firstElementChild;
    if (next) block.replaceWith(next);
    wireFormationBlock();
  }

  /** A formation's effect on a stat, as the violet "(+10%)" the battle log uses. Empty when the factor is 1. */
  function buff(factor: number | undefined): string {
    return factor !== undefined && factor !== 1 ? ` <span class="bl-buff">(${signedPct(factor)})</span>` : '';
  }

  /**
   * The force readout: power as the headline, march and haul as tiles, crew as a meter against its cap. Every figure
   * is bold and sits under a label that says what it is FOR; amber/red only where the player should act. Used by the
   * compose step and (compact) by the confirm step.
   */
  function statPanelHtml(p: PreviewResult, compact: boolean): string {
    const def = formations?.find((f) => f.id === p.formation);
    const base = formations?.find((f) => f.id === 'BALANCED');
    const why = (key: keyof Translations): string => (compact ? '' : `<div class="sa-why">${t(key)}</div>`);

    const over = p.crewUsed > p.crewCapacity;
    const pct = p.crewCapacity > 0 ? Math.min(100, (p.crewUsed / p.crewCapacity) * 100) : p.crewUsed > 0 ? 100 : 0;
    const crewState = over ? 'over' : p.crewUsed === 0 ? 'empty' : pct >= 85 ? 'high' : 'ok';
    const crewNote =
      crewState === 'over'
        ? t('worldmap.stat.crew.over', { n: p.crewUsed - p.crewCapacity })
        : crewState === 'high'
          ? t('worldmap.stat.crew.high')
          : crewState === 'empty'
            ? t('worldmap.stat.crew.empty')
            : t('worldmap.stat.crew.ok', { n: p.crewCapacity - p.crewUsed });

    const longMarch = p.marchSeconds > 3600;
    const haul = p.carryCapacity > 0;
    const lootChip =
      haul && def && base && def.lootShare !== base.lootShare
        ? `<div class="sa-sub"><span class="bl-buff">${t('worldmap.stat.haul.grab', { pct: Math.round(def.lootShare * 100) })}</span></div>`
        : '';

    return `
      <div class="sa-panel${compact ? ' sa-panel--compact' : ''}" data-stat-panel>
        <section class="sa-plate sa-hero" data-stat="power">
          <span class="sa-ico" aria-hidden="true">${icon('crosshair')}</span>
          <div class="min-w-0">
            <div class="sa-label">${t('worldmap.send.forcePower')}</div>
            <div class="sa-big sa-gold" data-stat-value>${Math.round(p.attackerPower).toLocaleString('en-GB')}${buff(def?.attackFactor)}</div>
            ${why('worldmap.stat.power.why')}
          </div>
        </section>
        <div class="sa-tiles">
          <section class="sa-plate${longMarch ? ' sa-plate--warn' : ''}" data-stat="march">
            <div class="sa-label"><span class="sa-ico sa-ico--sm" aria-hidden="true">${icon('clock')}</span>${t('worldmap.stat.march')}</div>
            <div class="sa-big" data-stat-value>${formatMarchTime(p.marchSeconds)}${buff(def?.marchTimeFactor)}</div>
            <div class="sa-sub">${t('worldmap.send.distanceTiles', { count: p.distanceTiles })}</div>
            <div class="sa-sub" data-preview-pace>${t('worldmap.send.pacedBy', { unit: formatUnitLabel(p.paceUnitType) })}</div>
            ${longMarch ? `<div class="sa-flag" data-stat-flag>${t('worldmap.stat.march.long')}</div>` : ''}
            ${why('worldmap.stat.march.why')}
          </section>
          <section class="sa-plate${haul ? '' : ' sa-plate--dim'}" data-stat="haul">
            <div class="sa-label"><span class="sa-ico sa-ico--sm" aria-hidden="true">${icon('crate')}</span>${t('worldmap.stat.haul')}</div>
            <div class="sa-big" data-stat-value>${haul ? t('worldmap.send.upTo', { count: p.carryCapacity }) : t('worldmap.stat.haul.none')}${haul ? buff(def?.carryFactor) : ''}</div>
            ${lootChip}
            ${why(haul ? 'worldmap.stat.haul.why' : 'worldmap.stat.haul.noneWhy')}
          </section>
        </div>
        <section class="sa-plate sa-crew sa-crew--${crewState}" data-stat="crew" data-crew-state="${crewState}">
          <div class="sa-crew-head">
            <div class="sa-label"><span class="sa-ico sa-ico--sm" aria-hidden="true">${icon('troops')}</span>${t('worldmap.stat.crew')}</div>
            <div class="sa-big sa-crew-fig" data-stat-value>${p.crewUsed} <span class="sa-of">/ ${p.crewCapacity}</span></div>
          </div>
          <div class="sa-meter" role="meter" aria-valuemin="0" aria-valuemax="${p.crewCapacity}" aria-valuenow="${p.crewUsed}" aria-label="${t('worldmap.stat.crew')}">
            <span class="sa-meter-fill" style="width:${pct}%"></span><span class="sa-meter-cap" aria-hidden="true"></span>
          </div>
          <div class="sa-crew-note" data-crew-note>${crewNote}</div>
          ${why('worldmap.stat.crew.why')}
        </section>
      </div>
    `;
  }

  function previewHtml(): string {
    if (previewLoading && !preview) {
      return `<div class="sa-plate sa-plate--dim text-text-dim text-xs">${t('worldmap.send.calculating')}</div>`;
    }
    if (previewError) {
      return `<div class="sa-plate sa-plate--warn text-crimson text-xs">${t('worldmap.send.previewFailed')}</div>`;
    }
    if (!preview) {
      return `<div class="sa-plate sa-plate--dim text-text-dim text-xs">${t('worldmap.send.previewHint')}</div>`;
    }
    // No odds are shown — the preview deliberately reveals nothing about the
    // target (see battle-api.ts's PreviewResult). Send recon to learn what is
    // waiting, or attack and find out.
    return `${statPanelHtml(preview, false)}<p class="text-[11px] text-text-dim mt-2" data-enemy-unknown>${t('worldmap.send.enemyUnknown')}</p>`;
  }

  function rosterRowsHtml(): string {
    const available = roster.filter((r) => !r.locked && r.quantityReserved > 0);
    if (!rosterLoaded) {
      return `<div class="text-text-dim text-xs">${t('worldmap.send.loadingRoster')}</div>`;
    }
    if (available.length === 0) {
      return `<div class="text-text-dim text-xs">${t('worldmap.send.noUnits')}</div>`;
    }
    return available
      .map((r) => {
        const art = unitArtFor(r.unitType);
        const current = quantities.get(r.unitType) ?? 0;
        return `
        <label class="flex items-center justify-between gap-3 py-1.5">
          <span class="flex items-center gap-2 min-w-0">
            ${art ? `<img src="${art.thumb}" alt="" aria-hidden="true" loading="lazy" class="w-9 h-9 shrink-0 object-contain" />` : ''}
            <span class="min-w-0">
              <span class="text-text text-sm truncate block">${formatUnitLabel(r.unitType)} <span class="text-text-dim text-xs">${t('worldmap.send.unitLevel', { level: r.level })}</span></span>
              <span class="text-text-dim text-[10px]">${t('worldmap.send.unitCrew', { count: r.crewSize })}${UNIT_CARRY_CAPACITY[r.unitType] > 0 ? ` &middot; ${t('worldmap.send.unitCarries', { count: UNIT_CARRY_CAPACITY[r.unitType] })}` : ''}</span>
            </span>
          </span>
          <input type="number" inputmode="numeric" min="0" max="${r.quantityReserved}" value="${current}"
            data-unit-type="${r.unitType}" data-max="${r.quantityReserved}"
            aria-label="${t('worldmap.send.qtyAria', { unit: formatUnitLabel(r.unitType), max: r.quantityReserved })}"
            class="send-army-qty w-24 min-h-11 px-2 py-1.5 bg-surface-2 text-text border border-border rounded outline-none text-right tabular-nums focus:border-gold"
            style="touch-action: manipulation;" />
        </label>
        <div class="text-[10px] text-text-dim -mt-1 mb-1 text-right">${current} / ${r.quantityReserved}</div>
        `;
      })
      .join('');
  }

  function startDisabled(): boolean {
    if (chosenUnits().length === 0) return true;
    if (preview && preview.crewUsed > preview.crewCapacity) return true;
    return false;
  }

  function composeBodyHtml(): string {
    const disabled = startDisabled();
    return `
      ${headerHtml(t('worldmap.send.title'))}
      <div class="flex flex-col gap-1" data-roster-rows>${rosterRowsHtml()}</div>
      <div class="mt-3" data-preview-block>${previewHtml()}</div>
      ${formationSelectorHtml()}
      <div class="sa-actions mt-6">
        <button type="button" data-action="cancel"
          class="font-button sa-cancel min-h-11 px-4 py-2 rounded"
          style="touch-action: manipulation;">${t('common.cancel')}</button>
        <button type="button" data-action="start" ${disabled ? 'disabled' : ''}
          class="font-button bg-gold sa-start min-h-12 px-4 py-2 rounded disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed"
          style="touch-action: manipulation;"><span class="sa-start-ico" aria-hidden="true">${icon('crosshair')}</span><span>${t('worldmap.send.start')}</span></button>
      </div>
    `;
  }

  function confirmBodyHtml(): string {
    const units = chosenUnits();
    const unitsList = units
      .map((u) => `<li class="flex items-center justify-between text-sm"><span>${formatUnitLabel(u.unitType)}</span><span class="tabular-nums">${u.quantity}</span></li>`)
      .join('');
    return `
      ${headerHtml(t('worldmap.send.confirmTitle'))}
      <ul class="flex flex-col gap-1 mb-3">${unitsList}</ul>
      ${
        preview
          ? `<div class="mb-3" data-confirm-stats>
          ${formations ? `<div class="flex items-center justify-between gap-2 mb-2" data-confirm-formation>
            <span class="text-xs text-text-dim">${t('worldmap.formation.label')}</span>
            <span class="text-xs font-bold text-gold-bright">${formationName(preview.formation)}</span>
          </div>` : ''}
          ${statPanelHtml(preview, true)}
        </div>`
          : ''
      }
      <p class="text-xs text-text-dim mb-3">${t('worldmap.send.confirmQuestion', { target: poi.colonyName ?? `(${poi.x}, ${poi.y})` })}</p>
      <div class="sa-actions mt-6">
        <button type="button" data-action="back" ${sending ? 'disabled' : ''}
          class="font-button sa-cancel min-h-11 px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          style="touch-action: manipulation;">${t('common.cancel')}</button>
        <button type="button" data-action="confirm" ${sending ? 'disabled' : ''}
          class="font-button bg-gold sa-start min-h-12 px-4 py-2 rounded disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed"
          style="touch-action: manipulation;"><span class="sa-start-ico" aria-hidden="true">${icon('crosshair')}</span><span>${sending ? t('worldmap.send.sending') : t('worldmap.send.yesSend')}</span></button>
      </div>
    `;
  }

  function render(): void {
    if (closed) return;
    const body = el.querySelector('[data-modal-body]') as HTMLDivElement;
    body.innerHTML = step === 'compose' ? composeBodyHtml() : confirmBodyHtml();
    wire();
  }

  function wire(): void {
    el.querySelector('[data-action="close"]')?.addEventListener('click', close);
    if (step === 'compose') {
      wireFormationBlock();
      el.querySelector('[data-action="cancel"]')?.addEventListener('click', close);
      el.querySelector('[data-action="start"]')?.addEventListener('click', () => {
        step = 'confirm';
        render();
      });
      el.querySelectorAll('.send-army-qty').forEach((node) => {
        const input = node as HTMLInputElement;
        input.addEventListener('input', () => {
          const max = Number(input.dataset.max ?? '0');
          let value = Math.floor(Number(input.value));
          if (Number.isNaN(value) || value < 0) value = 0;
          if (value > max) value = max;
          const unitType = input.dataset.unitType as UnitType;
          quantities.set(unitType, value);
          schedulePreview();
          // Re-render only the affected bits (start button + the N/max caption)
          // rather than the whole list, so the input the user is actively
          // typing into doesn't lose focus/caret position.
          const startBtn = el.querySelector('[data-action="start"]') as HTMLButtonElement | null;
          if (startBtn) startBtn.disabled = startDisabled();
          const caption = input.closest('label')?.nextElementSibling;
          if (caption) caption.textContent = `${value} / ${max}`;
        });
      });
    } else {
      el.querySelector('[data-action="back"]')?.addEventListener('click', () => {
        step = 'compose';
        render();
      });
      el.querySelector('[data-action="confirm"]')?.addEventListener('click', () => {
        void handleConfirm();
      });
    }
  }

  function schedulePreview(): void {
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      void runPreview();
    }, PREVIEW_DEBOUNCE_MS);
  }

  async function runPreview(): Promise<void> {
    const units = chosenUnits();
    if (units.length === 0) {
      preview = null;
      previewError = false;
      previewLoading = false;
      renderPreviewBlockOnly();
      return;
    }
    const seq = ++previewSeq;
    previewLoading = true;
    previewError = false;
    renderPreviewBlockOnly();
    try {
      const result = await previewAttack(poi.colonyId as string, units, formation);
      if (closed || seq !== previewSeq) return;
      preview = result;
      previewError = false;
    } catch {
      if (closed || seq !== previewSeq) return;
      previewError = true;
    } finally {
      if (!closed && seq === previewSeq) {
        previewLoading = false;
        renderPreviewBlockOnly();
      }
    }
  }

  function renderPreviewBlockOnly(): void {
    if (closed || step !== 'compose') return;
    const block = el.querySelector('[data-preview-block]');
    if (block) block.innerHTML = previewHtml();
    renderFormationBlockOnly(); // its "recon ignores this" note depends on the preview
    const startBtn = el.querySelector('[data-action="start"]') as HTMLButtonElement | null;
    if (startBtn) startBtn.disabled = startDisabled();
  }

  async function handleConfirm(): Promise<void> {
    if (sending || closed) return;
    const units = chosenUnits();
    if (units.length === 0) return;
    sending = true;
    render();
    try {
      const result = await sendArmy(poi.colonyId as string, units, formation);
      if (closed) return;
      showToast(t('worldmap.send.sent', { time: formatMarchTime(result.marchSeconds) }), 'success');
      close();
      onSent();
    } catch (err) {
      if (!closed) {
        const message = err instanceof Error ? err.message : t('worldmap.send.sendFailed');
        showToast(message, 'error');
        sending = false;
        render();
      }
    }
  }

  async function loadRoster(): Promise<void> {
    try {
      const fetched = await fetchRoster();
      if (closed) return;
      roster = fetched;
      rosterLoaded = true;
      render();
    } catch (err) {
      if (!closed) {
        rosterLoaded = true;
        roster = [];
        render();
        const message = err instanceof Error ? err.message : t('worldmap.send.rosterFailed');
        showToast(message, 'error');
      }
    }
  }

  async function loadFormations(): Promise<void> {
    try {
      const fetched = await fetchFormations();
      if (closed || !Array.isArray(fetched) || fetched.length === 0) return;
      formations = fetched;
      render();
    } catch {
      // The selector is an extra: without it the march is simply Balanced.
    }
  }

  render();
  void loadRoster();
  void loadFormations();

  return { close };
}
